import type {
  InstrumentDraftQuestion,
  InstrumentDraftQuestionType,
} from '@/features/instruments/infra/instrumentDraft.api'
import {
  defaultBuilderTextFormatFlags,
  splitWholeFieldMarkdown,
} from '@/features/instruments/domain/builderQuestionMarkdown'
import { renumberBuilderQuestionTitles, textResponseFromDraft } from '@/features/instruments/domain'
import { SLIDER_VALUE_MAX_CEILING } from '@/features/instruments/domain/sliderRange'
import type { BuilderQuestion, QuestionConfig, QuestionTypeId } from './types'
import { defaultConfigFor, isLikertPointCount, likertLabelsForPointCount } from './types'

function mapApiTypeToBuilder(t: InstrumentDraftQuestionType): QuestionTypeId | null {
  switch (t) {
    case 'multiple_choice':
      return 'MULTIPLE_CHOICE'
    case 'picture_choice':
      return 'PICTURE_CHOICE'
    case 'ranking':
      return 'RANKING'
    case 'yes_no':
      return 'YES_NO'
    case 'checkbox':
      return 'CHECKBOX'
    case 'dropdown':
      return 'DROPDOWN'
    case 'short_text':
      return 'SHORT_TEXT'
    case 'long_text':
      return 'LONG_TEXT'
    case 'date':
      return 'DATE'
    case 'rating':
      return 'RATING'
    case 'likert':
      return 'LIKERT'
    case 'slider':
      return 'SLIDER'
    case 'video':
      return 'VIDEO'
    default:
      return null
  }
}

function intToField(n: number | undefined): string {
  if (n === undefined || !Number.isFinite(n)) return ''
  return String(n)
}

function mapSelectionConfig(
  row: InstrumentDraftQuestion,
  base: QuestionConfig,
  type: 'MULTIPLE_CHOICE' | 'CHECKBOX',
): QuestionConfig {
  if (base.type !== type) return base
  const shared = {
    options: (row.options ?? []).map((o) => o.label),
    optionIds: (row.options ?? []).map((o) => o.id),
    minSelection: intToField(row.minSelections),
    maxSelection: intToField(row.maxSelections),
    randomize: row.randomizeOrder ?? false,
    layoutHorizontal: row.layoutHorizontal ?? base.data.layoutHorizontal,
    multiColumn: row.multiColumn ?? base.data.multiColumn,
    other: row.hasOtherOption ?? base.data.other,
    noneOfAbove: row.hasNoneOfAboveOption ?? base.data.noneOfAbove,
    textResponse: textResponseFromDraft(row.textResponse, row.hasOtherOption),
  }
  if (type === 'MULTIPLE_CHOICE' && base.type === 'MULTIPLE_CHOICE') {
    return { type: 'MULTIPLE_CHOICE', data: { ...base.data, ...shared } }
  }
  if (type === 'CHECKBOX' && base.type === 'CHECKBOX') {
    return {
      type: 'CHECKBOX',
      data: { ...base.data, ...shared, selectAllLabel: row.showSelectAll ?? false },
    }
  }
  return base
}

function draftOptionCaption(label = ''): string {
  const raw = label.trim()
  return raw.length > 0 ? raw : ''
}

function mapImageOptionsConfig(
  row: InstrumentDraftQuestion,
  base: QuestionConfig,
  type: 'PICTURE_CHOICE' | 'RANKING',
): QuestionConfig {
  if (base.type !== type) return base
  const mapped = (row.options ?? []).map((o) => ({
    id: o.id,
    imageUrl: o.imageUrl ?? '',
    caption: draftOptionCaption(o.label),
  }))
  const options =
    type === 'RANKING' && mapped.length < 2
      ? [
          ...mapped,
          ...base.data.options.slice(mapped.length).map((o) => ({
            id: o.id,
            imageUrl: '',
            caption: '',
          })),
        ].slice(0, 2)
      : mapped
  return {
    type,
    data: {
      imageSize: row.imageSize ?? base.data.imageSize,
      options,
    },
  }
}

function mapMultipleChoiceConfig(
  row: InstrumentDraftQuestion,
  base: QuestionConfig,
): QuestionConfig {
  return mapSelectionConfig(row, base, 'MULTIPLE_CHOICE')
}

function mapCheckboxConfig(row: InstrumentDraftQuestion, base: QuestionConfig): QuestionConfig {
  return mapSelectionConfig(row, base, 'CHECKBOX')
}

function mapDropdownConfig(row: InstrumentDraftQuestion, base: QuestionConfig): QuestionConfig {
  if (base.type !== 'DROPDOWN') return base
  return {
    type: 'DROPDOWN',
    data: {
      ...base.data,
      options: (row.options ?? []).map((o) => o.label),
      optionIds: (row.options ?? []).map((o) => o.id),
      randomize: row.randomizeOrder ?? false,
      other: row.hasOtherOption ?? base.data.other,
      noneOfAbove: row.hasNoneOfAboveOption ?? base.data.noneOfAbove,
    },
  }
}

function mapYesNoConfig(row: InstrumentDraftQuestion, base: QuestionConfig): QuestionConfig {
  if (base.type !== 'YES_NO') return base
  return {
    type: 'YES_NO',
    data: {
      ...base.data,
      yesLabel: row.yesLabel?.trim() || base.data.yesLabel,
      noLabel: row.noLabel?.trim() || base.data.noLabel,
      toggleStyle: row.yesNoToggleStyle ?? base.data.toggleStyle,
      layoutHorizontal: row.layoutHorizontal ?? base.data.layoutHorizontal,
    },
  }
}

function mapPictureChoiceConfig(
  row: InstrumentDraftQuestion,
  base: QuestionConfig,
): QuestionConfig {
  return mapImageOptionsConfig(row, base, 'PICTURE_CHOICE')
}

function mapRankingConfig(row: InstrumentDraftQuestion, base: QuestionConfig): QuestionConfig {
  return mapImageOptionsConfig(row, base, 'RANKING')
}

function mapTextConfig(
  row: InstrumentDraftQuestion,
  base: QuestionConfig,
  type: 'SHORT_TEXT' | 'LONG_TEXT',
): QuestionConfig {
  if (base.type !== type) return base
  return {
    type,
    data: {
      minLength: intToField(row.minLength),
      maxLength: intToField(row.maxLength),
      hintPlaceholder: row.placeholder?.trim() ?? '',
    },
  }
}

function mapDateConfig(row: InstrumentDraftQuestion, base: QuestionConfig): QuestionConfig {
  if (base.type !== 'DATE') return base
  return {
    type: 'DATE',
    data: {
      ...base.data,
      earliestSelectableDate: row.earliestSelectableDate?.trim() ?? '',
      latestSelectableDate: row.latestSelectableDate?.trim() ?? '',
      disablePastDates: row.disablePastDates ?? false,
      disableFutureDates: row.disableFutureDates ?? false,
      hintPlaceholder: row.placeholder?.trim() ?? '',
      displayFormat: row.dateInputFormat ?? base.data.displayFormat,
    },
  }
}

function mapRatingConfig(row: InstrumentDraftQuestion, base: QuestionConfig): QuestionConfig {
  if (base.type !== 'RATING') return base
  return {
    type: 'RATING',
    data: {
      scaleMax: row.ratingScaleMax ?? base.data.scaleMax,
      displayStyle: row.ratingDisplayStyle ?? base.data.displayStyle,
      lowLabel: row.ratingLowLabel?.trim() ?? '',
      highLabel: row.ratingHighLabel?.trim() ?? '',
    },
  }
}

function mapLikertConfig(row: InstrumentDraftQuestion, base: QuestionConfig): QuestionConfig {
  if (base.type !== 'LIKERT') return base
  const pointCount =
    row.likertPointCount != null && isLikertPointCount(row.likertPointCount)
      ? row.likertPointCount
      : base.data.pointCount
  const labelsSource = row.likertLabels?.length ? row.likertLabels : base.data.labels
  return {
    type: 'LIKERT',
    data: {
      pointCount,
      labels: likertLabelsForPointCount(pointCount, labelsSource),
      layoutHorizontal: row.likertLayoutHorizontal ?? base.data.layoutHorizontal,
    },
  }
}

function mapVideoConfig(row: InstrumentDraftQuestion, base: QuestionConfig): QuestionConfig {
  if (base.type !== 'VIDEO') return base
  const url = row.videoUrl?.trim() ?? ''
  return {
    type: 'VIDEO',
    data: {
      sourceKind: row.videoSource === 'upload' ? 'upload' : 'link',
      videoUrl: url,
      uploadedFileName: '',
      minLength: intToField(row.minLength),
      maxLength: intToField(row.maxLength),
      hintPlaceholder: row.placeholder?.trim() ?? '',
    },
  }
}

function mapSliderConfig(row: InstrumentDraftQuestion, base: QuestionConfig): QuestionConfig {
  if (base.type !== 'SLIDER') return base
  const rawMax = row.sliderMaxValue
  const clampedMax =
    rawMax == null ? undefined : Math.min(rawMax, SLIDER_VALUE_MAX_CEILING)
  return {
    type: 'SLIDER',
    data: {
      minValue: intToField(row.sliderMinValue),
      maxValue: intToField(clampedMax),
      step: intToField(row.sliderStep),
      lowLabel: row.sliderLowLabel?.trim() ?? '',
      highLabel: row.sliderHighLabel?.trim() ?? '',
    },
  }
}

const CONFIG_FROM_DRAFT: Record<
  QuestionTypeId,
  (row: InstrumentDraftQuestion, base: QuestionConfig) => QuestionConfig
> = {
  MULTIPLE_CHOICE: mapMultipleChoiceConfig,
  CHECKBOX: mapCheckboxConfig,
  DROPDOWN: mapDropdownConfig,
  YES_NO: mapYesNoConfig,
  PICTURE_CHOICE: mapPictureChoiceConfig,
  RANKING: mapRankingConfig,
  SHORT_TEXT: (row, base) => mapTextConfig(row, base, 'SHORT_TEXT'),
  LONG_TEXT: (row, base) => mapTextConfig(row, base, 'LONG_TEXT'),
  DATE: mapDateConfig,
  RATING: mapRatingConfig,
  LIKERT: mapLikertConfig,
  SLIDER: mapSliderConfig,
  VIDEO: mapVideoConfig,
}

function configFromDraft(row: InstrumentDraftQuestion, type: QuestionTypeId): QuestionConfig {
  const base = defaultConfigFor(type)
  return CONFIG_FROM_DRAFT[type](row, base)
}

function mapOneDraftQuestion(row: InstrumentDraftQuestion, displayIndex: number): BuilderQuestion {
  const type = mapApiTypeToBuilder(row.type)
  if (!type) {
    throw new Error(`Unsupported instrument draft question type: ${row.type}`)
  }
  const promptParts = splitWholeFieldMarkdown(row.prompt ?? '')
  const descRaw = row.description?.trim() ?? ''
  const descParts = descRaw ? splitWholeFieldMarkdown(descRaw) : { plain: '', fmt: defaultBuilderTextFormatFlags }

  return {
    id: row.id,
    title: `Question ${displayIndex + 1}`,
    type,
    questionText: promptParts.plain,
    questionTextFormat:
      promptParts.fmt.bold || promptParts.fmt.italic ? promptParts.fmt : undefined,
    description: descParts.plain,
    descriptionFormat:
      descParts.fmt.bold || descParts.fmt.italic ? descParts.fmt : undefined,
    mandatory: row.mandatory ?? false,
    hidden: row.hidden === true,
    config: configFromDraft(row, type),
    ...(row.fromTemplate ? { fromTemplate: true } : {}),
    ...(row.questionBankEntryId ? { questionBankEntryId: row.questionBankEntryId } : {}),
    ...(row.questionBankEntryId || row.isProfileUpdate ? { fromQuestionBank: true } : {}),
    ...(row.isProfileUpdate ? { isProfileUpdate: true } : {}),
    ...(row.profileAttributeId ? { profileAttributeId: row.profileAttributeId } : {}),
    ...(row.freshnessTag ? { freshnessTag: row.freshnessTag } : {}),
    ...(row.systemKey ? { systemKey: row.systemKey } : {}),
  }
}

/** Hydrates builder rows from campaign-service `GET …/draft` payload (inverse of `mapBuilderQuestionsToInstrumentDraft`). */
export function mapInstrumentDraftToBuilderQuestions(
  rows: readonly InstrumentDraftQuestion[],
): BuilderQuestion[] {
  const sorted = [...rows].sort((a, b) => a.order - b.order)
  const mapped = sorted.map((row, index) => mapOneDraftQuestion(row, index))
  return renumberBuilderQuestionTitles(mapped)
}
